import React, { useState, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Wand2, Loader, Save, CheckCircle, AlertTriangle, UploadCloud, FileText, X, Star, PlayCircle } from 'lucide-react';
import { useFirebase, useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { NavLink } from 'react-router-dom';
import CustomSelect from '../components/CustomSelect';
import { cpdCategories } from '../data/cpdData';

const CpdAssistantPage = ({ onLogCpd, initialLog, onClose }) => {
    const { user } = useAuth();
    const { storage } = useFirebase();
    const { subscription } = useSubscription();
    
    const [rawText, setRawText] = useState('');
    const [structuredData, setStructuredData] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    // State for the form fields
    const [activityStartDate, setActivityStartDate] = useState('');
    const [activityEndDate, setActivityEndDate] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(cpdCategories[0]);
    const [activityFormat, setActivityFormat] = useState('Online');
    const [evidenceFile, setEvidenceFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    // <-- STATE FOR VIDEO MODAL -->
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    useEffect(() => {
        // Don't do anything until subscription status is loaded, unless in edit mode
        if (!subscription && !initialLog) {
            return;
        }

        if (initialLog) {
            // EDIT MODE: Pre-fill the form for editing an existing log
            const initialCategory = cpdCategories.find(c => c.name === initialLog.activityType) || cpdCategories[0];
            setSelectedCategory(initialCategory);
            setStructuredData({
                learningGoal: initialLog.learningGoal || '',
                activityCompleted: initialLog.activityCompleted || '',
                providerSource: initialLog.providerSource || '',
                reflection: initialLog.reflection || '',
                impactOnPractice: initialLog.impactOnPractice || '',
                suggestedHours: initialLog.hours || 0.5,
            });
            setActivityStartDate(initialLog.activityStartDate || new Date().toISOString().split('T')[0]);
            setActivityEndDate(initialLog.activityEndDate || '');
            setActivityFormat(initialLog.activityFormat || 'Online');
            setStatus('success'); // Show the form immediately
        } else {
             // CREATE NEW MODE: Differentiate between Free and Pro
            if (subscription.isActive) {
                // Pro user: Show the AI assistant input
                setStatus('idle');
            } else {
                // Free user: Show the manual entry form immediately
                setStructuredData({ learningGoal: '', activityCompleted: '', providerSource: '', reflection: '', impactOnPractice: '', suggestedHours: 0.5 });
                setActivityStartDate(new Date().toISOString().split('T')[0]);
                setStatus('success');
            }
        }
    }, [initialLog, subscription]);

    const handleDataChange = (field, value) => {
        setStructuredData(prev => ({ ...prev, [field]: value }));
    };
    
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setEvidenceFile(e.target.files[0]);
        }
    };

    const handleAnalyze = async () => {
        if (!rawText.trim()) {
            setError("Please enter some notes about your activity.");
            return;
        }
        setStatus('loading');
        setError('');
        setStructuredData(null);
        setActivityStartDate(new Date().toISOString().split('T')[0]);

        try {
            const response = await fetch('/.netlify/functions/process-cpd-entry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rawText }),
            });

            if (!response.ok) throw new Error('Failed to get a response from the AI assistant.');
            
            const data = await response.json();
            setStructuredData(data);
            setSelectedCategory(cpdCategories.find(c => c.name === data.activityType) || cpdCategories[0]);
            setStatus('success');
        } catch (err) {
            console.error(err);
            setError('The AI assistant could not process your request. Please try again.');
            setStatus('error');
        }
    };

    const handleSaveLog = async () => {
        if (!structuredData || !activityStartDate) {
            setError("Please ensure all required fields, including the start date, are filled.");
            return;
        }
        setStatus('saving');
        setError('');

        let evidenceUrl = initialLog?.evidenceUrl || '';
        if (evidenceFile) {
            try {
                const docId = initialLog ? (initialLog.id || initialLog.articleId) : `custom-${new Date().getTime()}`;
                const filePath = `users/${user.uid}/cpd-evidence/${docId}/${evidenceFile.name}`;
                const storageRef = ref(storage, filePath);
                const uploadTask = uploadBytesResumable(storageRef, evidenceFile);

                await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
                        (error) => reject(error),
                        async () => {
                            evidenceUrl = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve();
                        }
                    );
                });
            } catch (uploadError) {
                setStatus('error');
                setError('Failed to upload your evidence file. Please try again.');
                return;
            }
        }

        try {
            const docId = initialLog ? (initialLog.id || initialLog.articleId) : null;
            await onLogCpd({
                id: docId,
                status: 'Completed',
                learningGoal: structuredData.learningGoal || '',
                activityCompleted: structuredData.activityCompleted || '',
                providerSource: structuredData.providerSource || '',
                activityStartDate,
                activityEndDate,
                evidenceUrl,
                hours: structuredData.suggestedHours || 0.5,
                activityType: selectedCategory.name,
                activityFormat,
                reflection: structuredData.reflection || '',
                impactOnPractice: structuredData.impactOnPractice || '',
            });
            setStatus('saved');
        } catch (err) {
            console.error("Failed to save CPD Log:", err);
            setError("Could not save the log. Please try again.");
            setStatus('error');
        }
    };
    
    if (status === 'saved') {
        return (
            <div className="text-center p-8 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                <h2 className="text-2xl font-bold text-green-800 dark:text-green-300">Log Saved Successfully!</h2>
                <button onClick={onClose} className="mt-6 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                    Return to Dashboard
                </button>
            </div>
        );
    }
    
    if (!subscription && !initialLog) {
        return <div className="p-8 text-center"><Loader className="animate-spin inline-block" /></div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center text-center mb-8">
                <div className="flex-1">
                    <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2 flex items-center justify-center">
                        <Wand2 size={36} className="mr-4"/> 
                        {initialLog ? 'Edit CPD Log' : 'Log CPD Activity'}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">
                        {initialLog ? 'Refine the details of your CPD activity.' : 'Log a new CPD activity manually or use the AI Assistant.'}
                    </p>
                </div>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800 dark:hover:text-white">
                    <X size={24} />
                </button>
            </div>

            {!initialLog && subscription?.isActive && (
                <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-300 dark:border-gray-700">
                    <label htmlFor="raw-text" className="block text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                        1. Describe your CPD Activity
                    </label>
                    <textarea id="raw-text" className="w-full h-32 p-3 bg-white dark:bg-gray-900 rounded-lg text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600"
                        placeholder="e.g., 'Attended the monthly SABR/SRS meeting and discussed new agenda items...'"
                        value={rawText} onChange={(e) => setRawText(e.target.value)} disabled={status === 'loading'}
                    />
                    <button onClick={handleAnalyze} disabled={status === 'loading' || !rawText.trim()} className="w-full mt-4 py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center">
                        {status === 'loading' ? <Loader size={20} className="animate-spin mr-2" /> : <Wand2 size={20} className="mr-2" />}
                        {status === 'loading' ? 'Analyzing...' : 'Analyze with AI'}
                    </button>
                </div>
            )}
            
            {!initialLog && !subscription?.isActive && (
                <div className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/30 border-2 border-dashed border-blue-400 dark:border-blue-700 rounded-lg">
                    <div className="grid md:grid-cols-2 gap-6 items-center">
                        <div>
                            <h3 className="text-2xl font-bold text-blue-800 dark:text-blue-200 flex items-center mb-2">
                                <Star size={24} className="mr-3 text-yellow-500"/> Supercharge with AI
                            </h3>
                            <p className="text-blue-700 dark:text-blue-300 mb-4">
                                You're using the manual CPD log. Upgrade to Pro to unlock the AI Assistant! Just paste your notes and watch it automatically structure your log, saving you valuable time.
                            </p>
                            <NavLink to="/pricing" className="inline-block px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                                Upgrade to Pro
                            </NavLink>
                        </div>
                        {/* <-- VIDEO MODAL TRIGGER --> */}
                        <button
                            onClick={() => setIsVideoModalOpen(true)}
                            className="relative bg-black rounded-lg h-48 w-full flex items-center justify-center cursor-pointer group hover:bg-gray-800 bg-cover bg-center"
                            style={{ backgroundImage: `url('https://firebasestorage.googleapis.com/v0/b/sabr-project-final.firebasestorage.app/o/promo-assets%2FLogging%20CPD%20shouldn’t%20be%20painful.png?alt=media&token=ac139ce5-91b2-4b18-a99a-ac36c732358b')` }}
                            aria-label="Play promo video"
                        >
                            <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                            <PlayCircle 
                                size={64} 
                                className="relative z-10 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-transform" 
                            />
                        </button>
                    </div>
                </div>
            )}

            {error && (<div className="mt-4 p-4 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 rounded-lg flex items-center"><AlertTriangle className="mr-3" /> {error}</div>)}

            {status === 'success' && structuredData && (
                <div className="mt-8">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                        {initialLog ? 'Edit Activity Details' : (subscription?.isActive ? '2. Review, Edit, and Save Your Log' : 'Manually Enter Your CPD Activity')}
                    </h2>
                    <div className="space-y-4 bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg border border-gray-700">
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Learning Goal</label>
                            <textarea value={structuredData.learningGoal || ''} onChange={(e) => handleDataChange('learningGoal', e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600" rows={2}/>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Activity Start Date*</label>
                                <input type="date" value={activityStartDate} onChange={(e) => setActivityStartDate(e.target.value)} required className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600"/>
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Activity End Date</label>
                                <input type="date" value={activityEndDate} onChange={(e) => setActivityEndDate(e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600"/>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Completed Activity Description</label>
                            <textarea value={structuredData.activityCompleted || ''} onChange={(e) => handleDataChange('activityCompleted', e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600" rows={3}/>
                        </div>

                         <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Provider / Source</label>
                            <input type="text" value={structuredData.providerSource || ''} onChange={(e) => handleDataChange('providerSource', e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600"/>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <div>
                                <CustomSelect
                                    label="Category"
                                    options={cpdCategories}
                                    selected={selectedCategory}
                                    setSelected={setSelectedCategory}
                                />
                             </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Hours</label>
                                <input type="number" step="0.25" value={structuredData.suggestedHours || ''} onChange={(e) => handleDataChange('suggestedHours', e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600"/>
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reflection on Learning</label>
                            <textarea value={structuredData.reflection || ''} onChange={(e) => handleDataChange('reflection', e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600" rows={4}/>
                        </div>

                         <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Impact on Practice</label>
                            <textarea value={structuredData.impactOnPractice || ''} onChange={(e) => handleDataChange('impactOnPractice', e.target.value)} className="w-full p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-300 dark:border-gray-600" rows={3}/>
                        </div>
                        
                        <div>
                            <CustomSelect
                                label="Activity Format/Location"
                                options={['Online', 'In-Person', 'Local', 'International']}
                                selected={activityFormat}
                                setSelected={setActivityFormat}
                            />
                        </div>
                        
                        <div>
                           <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Upload Evidence</label>
                           <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md">
                                <div className="space-y-1 text-center">
                                    <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                                    <div className="flex text-sm text-gray-600 dark:text-gray-400">
                                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white dark:bg-gray-900 rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
                                            <span>Upload a file</span>
                                            <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB</p>
                                </div>
                            </div>
                             {evidenceFile && (
                                <div className="mt-2 text-sm flex items-center text-gray-700 dark:text-gray-300">
                                    <FileText className="mr-2 h-5 w-5"/>
                                    <span>{evidenceFile.name}</span>
                                </div>
                            )}
                            {initialLog && initialLog.evidenceUrl && !evidenceFile && (
                                <div className="mt-2 text-sm">
                                    <a href={initialLog.evidenceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                                        <FileText className="mr-2 h-5 w-5"/>
                                        View current evidence
                                    </a>
                                </div>
                            )}
                            {status === 'saving' && uploadProgress > 0 && (
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mt-2">
                                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                            )}
                        </div>

                        <button onClick={handleSaveLog} disabled={status === 'saving'} className="w-full mt-2 py-3 px-4 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center">
                            {status === 'saving' ? <Loader size={20} className="animate-spin mr-2" /> : <Save size={20} className="mr-2" />}
                            {status === 'saving' ? 'Saving...' : 'Save Log'}
                        </button>
                    </div>
                </div>
            )}

            {/* <-- VIDEO MODAL POP-UP --> */}
            {isVideoModalOpen && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
                    onClick={() => setIsVideoModalOpen(false)}
                >
                    <div 
                        className="bg-black p-2 rounded-lg shadow-2xl w-full max-w-3xl mx-4 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            onClick={() => setIsVideoModalOpen(false)}
                            className="absolute -top-2 -right-2 z-10 bg-white rounded-full p-1 text-gray-800 hover:bg-gray-200"
                            aria-label="Close video player"
                        >
                            <X size={20} />
                        </button>
                        <div className="aspect-video">
                            <video
                                className="w-full h-full rounded-md"
                                src="https://firebasestorage.googleapis.com/v0/b/sabr-project-final.firebasestorage.app/o/promo-assets%2FLogging%20CPD%20shouldn’t%20be%20painful.mp4?alt=media&token=87907009-961e-4cf7-b2b7-adc9f69dc808"
                                controls
                                autoPlay
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CpdAssistantPage;